// React
import * as React from "react";

// Amplify UI
import {
    Flex,
    Loader,
    TabItem,
    Tabs,
    View,
    useTheme,
} from "@aws-amplify/ui-react";

// Components
import { liveChannelService, mediaService } from "../../services";
import { LiveEventsCollection } from "../live-events-collection";
import { ILiveStream, IMemberPlans } from "../../types";
import { Layout1 } from "../layouts-live-stream/Layout1";
import { Layout2 } from "../layouts-live-stream/Layout2";
import { Layout3 } from "../layouts-live-stream/Layout3";
import { Layout4 } from "../layouts-live-stream/Layout4";
import { Layout5 } from "../layouts-live-stream/Layout5";
import { Layout6 } from "../layouts-live-stream/Layout6";
import { Layout7 } from "../layouts-live-stream/Layout7";
import { Layout8 } from "../layouts-live-stream/Layout8";
import { Layout1 as MobileLivestreamLayout } from "../layouts-live-stream-mobile/Layout1";
import { useIsMobile } from "../hooks";
import moment from "moment";

const initiateWebSocket = (clientId: string) =>
    new WebSocket(
        `${process.env.REACT_APP_STREAM_STATUS_WEBSOCKET_URL!}?clientId=${clientId}`
    );

// Map of template IDs to layout components
const LAYOUT_COMPONENTS: Record<number, React.FC<any>> = {
    0: Layout1,
    1: Layout2,
    2: Layout3,
    3: Layout4,
    4: Layout5,
    5: Layout6,
    6: Layout7,
    7: Layout8,
};

export const EventTabs: React.FC<{
    clientId: string;
    sessionToken: string;
    isSignedIn: boolean;
    enableTabControls: boolean;
    enableShareControls: boolean;
    setShowPlayerModal: (value: boolean) => void;
    setCurrentlyStreamingEvent: (value: ILiveStream | undefined) => void;
    templateId?: number;
    externalLiveEvents?: ILiveStream[];
}> = ({
    clientId,
    sessionToken,
    isSignedIn,
    enableTabControls,
    enableShareControls,
    templateId,
    externalLiveEvents,
    setShowPlayerModal,
    setCurrentlyStreamingEvent,
}) => {
        const { tokens } = useTheme();
        const isMobile = useIsMobile();
        const [ws, setWs] = React.useState<WebSocket>();
        const [index, setIndex] = React.useState<string | number>(0);
        const [showLoader, setShowLoader] = React.useState(true);
        const [events, setEvents] = React.useState<ILiveStream[]>([]);
        const [activePlans, setActivePlans] = React.useState<IMemberPlans[]>([]);
        const [hasFullAccess, setHasFullAccess] = React.useState<boolean>(false);
        const [freeTrialPlanIds, setFreeTrialPlanIds] = React.useState<string[]>([]);

        const getAllLiveEvents = React.useCallback(async () => {
            try {

                const liveEvents = externalLiveEvents?.map((item) => ({
                    ...item,
                    streamingUrl: item.stream?.playbackUrl
                }));

                console.log('liveEvents', liveEvents)
                
                if (!liveEvents) {
                    setEvents([]);
                    return;
                }

                setEvents(
                    liveEvents
                        .sort((a, b) =>
                            new Date(a.date).getTime() < new Date(b.date).getTime() ? -1 : 1
                        )
                        .filter((el) => !!el.id)
                );
            } catch (err) {
                console.log(err);
            } finally {
                setShowLoader(false);
            }
        }, [externalLiveEvents]);

        const fetchCustomerActivePlans = React.useCallback(async () => {
            const result = await liveChannelService.getCustomerPurchasedPlans(sessionToken!);
            setActivePlans(
                result.filter((item) =>
                    item.expiryDate ? new Date(item.expiryDate).getTime() > Date.now() : true
                )
            );

            const trialInfo = await mediaService.checkTrialCodeUser(sessionToken!);
            if (trialInfo.length > 0) {
                setFreeTrialPlanIds(trialInfo[0].planId);
                setHasFullAccess(trialInfo[0].isFullAccess);
            }
        }, [sessionToken]);

        const generateLoadSignInModalEvent = React.useCallback(
            (event: ILiveStream, showPurchasePlanModal?: boolean) => {
                const displayLoginEvent = new CustomEvent("loadSignInModal", {
                    detail: {
                        eventRequested: event,
                        showPurchasePlanModal: showPurchasePlanModal,
                        requestedPlanType: 2,
                    },
                });

                window.dispatchEvent(displayLoginEvent);
            },
            []
        );

        const handleWatchClick = React.useCallback(
            async (event: ILiveStream) => {
                if (event.isPrivate) {
                    if (isSignedIn) {
                        const hasAccess = !!event.planDetails.length
                            ? !!activePlans.filter((memberPlan) =>
                                event.planDetails.some((eventPlan) => eventPlan?.id === memberPlan.id)
                            ).length || freeTrialPlanIds.includes(event.id) || hasFullAccess
                            : true;

                        if (hasAccess) {
                            if (
                                moment(moment(event.eventStartDate, moment.ISO_8601)).isBefore(
                                    moment(new Date(), moment.ISO_8601),
                                    "day"
                                ) || event.streamingUrl
                            ) {
                                try {
                                    const token = await liveChannelService.getPlaybackToken(event.id, sessionToken);
                                    setShowPlayerModal(true);

                                    const eventWithToken = {
                                        ...event,
                                        streamingUrl: `${event.streamingUrl}?token=${token}`,
                                    };
                                    setCurrentlyStreamingEvent(eventWithToken);
                                } catch (err) {
                                    console.log("Error while fetching token is: ", err);
                                }
                            } else {
                                console.log("Event has not started yet.");
                            }
                        } else {
                            generateLoadSignInModalEvent(event, true);
                        }
                    } else {
                        generateLoadSignInModalEvent(event);
                    }
                } else {
                    if (
                        moment(moment(event.eventStartDate, moment.ISO_8601)).isBefore(
                            moment(new Date(), moment.ISO_8601),
                            "day"
                        ) || event.streamingUrl
                    ) {
                        setShowPlayerModal(true);
                        setCurrentlyStreamingEvent(event);
                    } else {
                        console.log("Event has not started yet.");
                    }
                }
            },
            [activePlans, isSignedIn, sessionToken, hasFullAccess, freeTrialPlanIds, generateLoadSignInModalEvent, setCurrentlyStreamingEvent, setShowPlayerModal]
        );

        React.useEffect(() => {
            if (clientId && !ws) {
                setWs(initiateWebSocket(clientId));
            }
        }, [clientId, ws]);

        React.useEffect(() => {
            window.addEventListener("onFetchUserPlansEvent", () => {
                fetchCustomerActivePlans();
            });
        }, [fetchCustomerActivePlans]);

        React.useEffect(() => {
            if (isSignedIn && sessionToken) {
                fetchCustomerActivePlans();
            }
        }, [fetchCustomerActivePlans, isSignedIn, sessionToken]);

        React.useEffect(() => {
            const onClose = () => {
                console.log("Called on Close");
                setTimeout(() => {
                    setWs(initiateWebSocket(clientId));
                }, 5000);
            };

            if (ws) {
                ws.onmessage = () => {
                    getAllLiveEvents();
                };

                ws.onopen = (event) => {
                    console.log("Connected to socket", event);
                };

                ws.addEventListener("close", onClose);
            }

            return () => {
                if (ws) {
                    ws.removeEventListener("close", onClose);
                }
            };
        }, [clientId, getAllLiveEvents, ws]);

        React.useEffect(() => {
            getAllLiveEvents();
        }, [getAllLiveEvents]);

        if (showLoader) {
            return (
                <Flex justifyContent={"center"} alignItems={"center"} height={"100%"}>
                    <View padding={tokens.space.large}>
                        <Loader size="large" />
                    </View>
                </Flex>
            );
        }

        const LayoutComponent = templateId !== undefined && templateId >= 0 ? LAYOUT_COMPONENTS[templateId] : null;

        const layoutProps = {
            events,
            sessionToken,
            isSignedIn,
            activePlans,
            hasFullAccess,
            freeTrialPlanIds,
            onWatchClick: handleWatchClick,
        };

        return (
            <>
                <div>
                    {templateId === -1 ? (
                        // TODO: Will be absolute soon
                        enableTabControls ? (
                            <Tabs currentIndex={index} onChange={(i: number | string) => setIndex(i)}>
                                <TabItem title="ALL">
                                    <LiveEventsCollection
                                        events={events}
                                        sessionToken={sessionToken}
                                        isSignedIn={isSignedIn}
                                    />
                                </TabItem>
                                <TabItem title="FREE">
                                    <LiveEventsCollection
                                        events={events.filter((el) => !el.isPrivate)}
                                        sessionToken={sessionToken}
                                        isSignedIn={isSignedIn}
                                    />
                                </TabItem>
                                <TabItem title="PREMIUM">
                                    <LiveEventsCollection
                                        events={events.filter((el) => el.isPrivate)}
                                        sessionToken={sessionToken}
                                        isSignedIn={isSignedIn}
                                    />
                                </TabItem>
                            </Tabs>
                        ) : (
                            <LiveEventsCollection
                                events={events}
                                sessionToken={sessionToken}
                                isSignedIn={isSignedIn}
                            />
                        )
                    ) : isMobile ? (
                        <div style={{ paddingTop: '30px' }}>
                            <MobileLivestreamLayout {...layoutProps} />
                        </div>
                    ) : LayoutComponent ? (
                        <div style={{ paddingTop: templateId === 2 ? '0px' : '30px', paddingBottom: templateId === 1 ? '30px' : '0px' }}>
                            <LayoutComponent {...layoutProps} />
                        </div>
                    ) : null}
                </div>

            </>
        );
    };
